import {
  PackageType,
  SelectedExperienceType,
} from '@features/DynamicPackagesConfigurationWizard/types';
import { getDatesRange } from '@utils/helpers';
import { some } from '@utils/some';
import { Moment } from 'moment';
import { useMemo } from 'react';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import type {
  ExperiencesByDay,
  GroupedExperienceOption,
} from '../ExperiencesStep/RepeatingExperiencesStep/types';

export const useGroupDiningOptionsByDateAndOptionGroup = <T extends { dayNumber?: number }>(
  startDate: Moment,
  endDate: Moment,
  options: T[]
) => {
  const stayDuration = endDate
    .add(1, 'd')
    .diff(startDate.startOf('d').format('YYYY-MM-DD'), 'days');
  const dates = getDatesRange(startDate, stayDuration);
  const groupedOptions = useMemo(() => {
    const mappedDiningOptions = options.reduce(
      (
        groupedOptions: Array<{
          options: T[];
          date: string;
          isoDate: string;
        }>,
        option
      ) => {
        const day = option.dayNumber ?? 0;
        groupedOptions[day ?? 0]?.options.push(option);
        return groupedOptions;
      },
      dates.map(({ date }) => ({
        options: [],
        date: date.format('DD MMM yyyy'),
        isoDate: date.format('YYYY-MM-DD'),
      }))
    );
    return mappedDiningOptions;
  }, [dates, options]);

  return groupedOptions;
};

const enrichWithConfigurationGroups = (
  options: GroupedExperienceOption[],
  configurationGroups?: ConfigurationGroupType[]
) => {
  if (!configurationGroups) return;
  options.forEach((optionGroup) => {
    const configGroup = configurationGroups.find((cg) => cg.id === optionGroup.optionGroupId);
    if (configGroup) {
      optionGroup.minimumSelections = configGroup.minimumSelections;
      optionGroup.maximumSelections = configGroup.maximumSelections;
    }
  });
};

const buildDateRangeLabels = (startDate: Moment, dayNumbers: number[]): string[] =>
  dayNumbers.map((dayNum) => startDate.clone().add(dayNum, 'days').format('YYYY-MM-DD'));

const sortOptionsByIncluded = (options: GroupedExperienceOption[]) => {
  options.sort((a, b) => (a.isIncluded === b.isIncluded ? 0 : a.isIncluded ? 1 : -1));
};

const groupRepeatingByDate = (
  startDate: Moment,
  selectedExperiences: SelectedExperienceType[],
  stayDuration: number,
  numberOfGuests: number,
  configurationGroups?: ConfigurationGroupType[]
): ExperiencesByDay[] => {
  const dates = getDatesRange(startDate, stayDuration + 1);
  const datesWithOptions: { date: string; options: GroupedExperienceOption[] }[] = dates.map(
    ({ date }) => ({
      date: date.format('DD MMM yyyy'),
      options: [],
    })
  );

  selectedExperiences.forEach((opt) => {
    const dayIndex = opt.dayNumber ?? 0;
    const isIncluded = opt.isIncluded ?? false;
    const dayNumberRange = opt.dayNumberRange;
    const optionGroupId = Number(opt.optionGroupId);
    const isChosenByAllGuests = opt.count === numberOfGuests;

    const date = datesWithOptions[dayIndex];
    if (!date) return;

    const existingOptions = date.options.find(
      (o) =>
        o?.optionGroupId === optionGroupId && o.isIncluded === (isIncluded || isChosenByAllGuests)
    );

    if (existingOptions) {
      existingOptions.options.push(opt);
    } else {
      date.options.push({
        dayNumber: dayIndex,
        dayNumberRange,
        isIncluded: isIncluded || isChosenByAllGuests,
        optionGroupId,
        options: [opt],
      });
    }
  });

  return datesWithOptions
    .filter((day) => some(day.options))
    .map((day) => {
      const dateRange = buildDateRangeLabels(startDate, day.options[0]?.dayNumberRange || []);
      enrichWithConfigurationGroups(day.options, configurationGroups);
      sortOptionsByIncluded(day.options);
      return { ...day, dateRange };
    });
};

const shouldUseSingleDayCollapse = (selectedExperiences: SelectedExperienceType[]): boolean => {
  if (selectedExperiences.length === 0) return true;
  const first = selectedExperiences[0]?.dayNumberRange;
  if (!first || first.length !== 1) return false;
  const targetDay = first[0];
  return selectedExperiences.every((exp) => {
    const range = exp.dayNumberRange;
    return range && range.length === 1 && range[0] === targetDay;
  });
};

export const groupRepeatingByExperience = (
  startDate: Moment,
  selectedExperiences: SelectedExperienceType[],
  stayDuration: number,
  numberOfGuests: number,
  configurationGroups?: ConfigurationGroupType[]
): ExperiencesByDay[] => {
  if (shouldUseSingleDayCollapse(selectedExperiences)) {
    return groupRepeatingByDate(
      startDate,
      selectedExperiences,
      stayDuration,
      numberOfGuests,
      configurationGroups
    );
  }

  const dates = getDatesRange(startDate, stayDuration + 1);
  type RepeatingEntry = {
    dayIndex: number;
    date: string;
    groupKey: string;
    options: GroupedExperienceOption[];
  };
  const entries: RepeatingEntry[] = [];

  selectedExperiences.forEach((opt) => {
    const dayIndex = opt.dayNumber ?? 0;
    const isIncluded = opt.isIncluded ?? false;
    const dayNumberRange = opt.dayNumberRange;
    const optionGroupId = Number(opt.optionGroupId);
    const isChosenByAllGuests = opt.count === numberOfGuests;
    const effectiveIsIncluded = isIncluded || isChosenByAllGuests;

    if (dayIndex >= dates.length) return;
    const dateLabel = dates[dayIndex].date.format('DD MMM yyyy');
    const groupKey = `${dayIndex}-${optionGroupId}-${effectiveIsIncluded ? 'inc' : 'opt'}`;

    const existingEntry = entries.find((e) => e.groupKey === groupKey);
    if (existingEntry) {
      existingEntry.options[0].options.push(opt);
    } else {
      entries.push({
        dayIndex,
        date: dateLabel,
        groupKey,
        options: [
          {
            dayNumber: dayIndex,
            dayNumberRange,
            isIncluded: effectiveIsIncluded,
            optionGroupId,
            options: [opt],
          },
        ],
      });
    }
  });

  entries.sort((a, b) => {
    if (a.dayIndex !== b.dayIndex) return a.dayIndex - b.dayIndex;
    const aIncluded = a.options[0].isIncluded ? 1 : 0;
    const bIncluded = b.options[0].isIncluded ? 1 : 0;
    return aIncluded - bIncluded;
  });

  return entries.map((entry) => {
    const dateRange = buildDateRangeLabels(startDate, entry.options[0]?.dayNumberRange || []);
    enrichWithConfigurationGroups(entry.options, configurationGroups);
    return {
      date: entry.date,
      groupKey: entry.groupKey,
      options: entry.options,
      dateRange,
    };
  });
};

export const useGroupOptionsByDateAndOptionGroup = (
  startDate: Moment,
  selectedExperiences: SelectedExperienceType[],
  packageType: PackageType,
  stayDuration: number,
  numberOfGuests: number,
  configurationGroups?: ConfigurationGroupType[],
  isRepeatedFlexibleEnabled = false
): ExperiencesByDay[] => {
  if (packageType === PackageType.REPEATING) {
    return isRepeatedFlexibleEnabled
      ? groupRepeatingByExperience(
          startDate,
          selectedExperiences,
          stayDuration,
          numberOfGuests,
          configurationGroups
        )
      : groupRepeatingByDate(
          startDate,
          selectedExperiences,
          stayDuration,
          numberOfGuests,
          configurationGroups
        );
  }

  const dates = getDatesRange(startDate, stayDuration + 1);

  const selectedExperiencesByOptionGroup: { [key: string]: SelectedExperienceType[] } =
    selectedExperiences.reduce((groups, experience) => {
      if (experience.isIncluded) {
        groups[experience.optionId] = [experience];
        return groups;
      }

      if (!experience.optionGroupId) {
        return groups;
      }

      groups[experience.optionGroupId] = groups[experience.optionGroupId] || [];
      groups[experience.optionGroupId].push(experience);
      return groups;
    }, {} as { [key: string]: SelectedExperienceType[] });

  const optionGroups = Object.values(selectedExperiencesByOptionGroup).reduce(
    (
      acc: {
        options: SelectedExperienceType[];
        isIncluded: boolean;
        dayNumber: number;
        dayNumberRange: number[];
        optionGroupId: number;
      }[],
      group: SelectedExperienceType[]
    ) => {
      const experiencesChosenByAllGuests =
        group.filter((item) => item.count === numberOfGuests) || [];
      const otherExperiences = group.filter((item) => item.count !== numberOfGuests);
      experiencesChosenByAllGuests.forEach((exp) => {
        acc.push({
          options: [exp],
          isIncluded: true,
          dayNumber: group[0].dayNumber,
          dayNumberRange: group[0].dayNumberRange || [group[0].dayNumber],
          optionGroupId: Number(group[0].optionGroupId),
        });
      });
      if (some(otherExperiences)) {
        acc.push({
          options: otherExperiences,
          isIncluded: group.every((item) => item.isIncluded === true),
          dayNumber: group[0].dayNumber,
          dayNumberRange: group[0].dayNumberRange || [group[0].dayNumber],
          optionGroupId: Number(group[0].optionGroupId),
        });
      }
      return acc;
    },
    []
  );

  const groupedOptions = useMemo(() => {
    const maxDayNumber = optionGroups.reduce(
      (max, option) => Math.max(max, option.dayNumber ?? 0),
      0
    );
    const requiredLength = Math.max(dates.length, maxDayNumber + 1);

    const initialArray = Array.from({ length: requiredLength }, (_, index) => {
      const dateForDay = startDate.clone().add(index, 'days');
      return {
        options: [] as GroupedExperienceOption[],
        date: dateForDay.format('DD MMM yyyy'),
        dateRange: [dateForDay.format('YYYY-MM-DD')],
      };
    });

    const mappedOptions = optionGroups.reduce((groupedOptions, option) => {
      const day = option.dayNumber ?? 0;
      groupedOptions[day]?.options.push(option);
      return groupedOptions;
    }, initialArray);

    const filtered = mappedOptions.filter((day) => some(day.options));

    if (configurationGroups) {
      filtered.forEach((day) => enrichWithConfigurationGroups(day.options, configurationGroups));
    }

    return filtered;
  }, [dates, optionGroups, startDate, configurationGroups]);
  return groupedOptions;
};
